import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import rtl from 'jss-rtl';
import UserSessionTimeoutMessage from './UserSessionTimeoutMessage';
import { baseTheme } from '../styles';
import { BrowserSuggestion } from './BrowserSuggestion';
import { create } from 'jss';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@material-ui/core';
import { FullPageLoader } from '../../common/components';
import { isArabicLocale } from '../../common/utils/checkLocalePlayer';
import { jssPreset, StylesProvider, useTheme } from '@material-ui/core/styles';
import { loadFont } from '../utils/loadFont';
import { RouteProps } from 'react-router-dom';
import { SessionStore } from '../../constants/sessionStore';
import { SHLApp } from '../../constants/shlapp';
import { useAxiosInterceptor } from '../../common/utils/interceptor';
import { useBasicDetails } from '../../store/storeApi';
import { useTranslation } from 'react-i18next';
import '../styles/base.css';
import Timeline from '../../verticalTransition/components/Timeline';
import { TemplateLayout } from '../../store/interfaces';
import { getTemplateLayout } from '../../common/utils/FallbackCondition';

const Content = React.lazy(() => import('./Content'));
const Header = React.lazy(() => import('./Header'));
const Footer = React.lazy(() => import('./Footer'));
const AppPopup = React.lazy(() => import('../../shlApp/components/AppPopup'));
const PostHireFooter = React.lazy(() => import('./PostHireFooter'));

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
/** Base Component for initializing theme having header content and footer */
const Base: React.FC = (props: RouteProps): JSX.Element => {
  const {
    template: { settings, layoutCode, type },
    isUserInactive,
  } = useBasicDetails();
  const layout = getTemplateLayout(layoutCode);

  /** Calling the axios intercepter hook */
  useAxiosInterceptor();

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('xs'));
  const userAgentBool = navigator.userAgent.indexOf(SHLApp.IS_SHLAPP_AGENT) === -1;
  const isProceedToBrowser = sessionStorage.getItem(SessionStore.proceedToBrowser) === 'true';

  const { t } = useTranslation();

  /** setting direction attribute on html/body */
  useLayoutEffect(() => {
    const htmlTag = document.getElementsByTagName('html')[0];
    const bodyTag = document.getElementsByTagName('body')[0];
    const direction = isArabicLocale() ? 'rtl' : 'ltr';
    htmlTag.setAttribute('dir', direction);
    bodyTag.setAttribute('dir', direction);
    document.title = settings.mfsEnabled ? t('shl360') : t('shlExperiences');
  }, [t, settings]);

  useEffect(() => {
    loadFont(settings.page.fontType);
  }, [settings.page.fontType]);

  return (
    <StylesProvider jss={jss}>
      <ThemeProvider theme={baseTheme(settings, type)}>
        <CssBaseline />
        <Suspense fallback={<FullPageLoader isLoading />}>
          <Header />
          {!isProceedToBrowser && !settings.mfsEnabled && smDown && userAgentBool && <AppPopup />}
          {isUserInactive ? <UserSessionTimeoutMessage /> : <BrowserSuggestion />}
          {layout === TemplateLayout.TEMPLATE_LAYOUT_02 ? <Timeline /> : <></>}
          <Content>{props.children}</Content>
          {settings.mfsEnabled ? <PostHireFooter /> : <Footer />}
        </Suspense>
      </ThemeProvider>
    </StylesProvider>
  );
};

export default Base;
